// ============================================================
// MyGene — PageContainer
// Wraps all page content with consistent header + spacing.
// Use on every page that renders inside Layout.
//
// Usage:
//   <PageContainer title="Patients" subtitle="Manage patient records">
//     <Card>...</Card>
//   </PageContainer>
// ============================================================

import React, { ReactNode } from "react";
import { Breadcrumb, Button, Space, Typography } from "antd";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  type?: "primary" | "default" | "dashed" | "text";
  danger?: boolean;
}

interface PageContainerProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: PageAction[];
  children: ReactNode;
  /** Removes the default 24px gap between child elements */
  noGap?: boolean;
  /** Adds a max-width cap for very wide monitors */
  maxWidth?: number;
  /** Optional e2e test anchor placed on the outermost container div. */
  "data-testid"?: string;
}

const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
  noGap = false,
  maxWidth,
  "data-testid": dataTestId,
}) => {
  const navigate = useNavigate();
  const hasHeader = title || breadcrumbs?.length || actions?.length;

  return (
    <div
      className="mg-page-container"
      data-testid={dataTestId}
      style={{
        width: "100%",
        maxWidth: maxWidth ?? "none",
        margin: maxWidth ? "0 auto" : undefined,
      }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb
          style={{ marginBottom: 8 }}
          items={breadcrumbs.map((b, i) => ({
            title: b.path && i < breadcrumbs.length - 1
              ? <span
                  onClick={() => navigate(b.path!)}
                  style={{ cursor: "pointer", color: "var(--mg-primary)" }}
                >
                  {b.label}
                </span>
              : b.label,
          }))}
        />
      )}

      {/* Page header */}
      {hasHeader && (
        <div
          className="mg-page-header"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            {title && (
              <Title
                level={4}
                style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}
              >
                {title}
              </Title>
            )}
            {subtitle && (
              <Text type="secondary" style={{ fontSize: 11, marginTop: 2, display: "block" }}>
                {subtitle}
              </Text>
            )}
          </div>

          {actions && actions.length > 0 && (
            <Space wrap>
              {actions.map((a, i) => (
                <Button
                  key={i}
                  type={a.type ?? "default"}
                  icon={a.icon}
                  danger={a.danger}
                  onClick={a.onClick}
                >
                  {a.label}
                </Button>
              ))}
            </Space>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className="mg-page-content"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: noGap ? 0 : 16,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PageContainer;

// ── Convenience re-export of all shared components ──────────
export { default as SectionCard }   from "./SectionCard";
export { default as DataTable }     from "./DataTable";
export { default as StatusTag }     from "./StatusTag";
export { default as EmptyState }    from "./EmptyState";
export { default as InlineLoading } from "./InlineLoading";